import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowsRotate,
  faCheckCircle,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, TextButton, TextLink } from '@modusign/design-system';
import { useDispatch, useSelector } from 'react-redux';

import LoadingStatus from '@libs/loading-status';

import {
  getExternalDocumentTotalBatchUpdateCount,
  initializeExternalDocumentBatchJob,
} from '../../state';

export default function BatchUpdateSnackBar() {
  const count = useSelector(getExternalDocumentTotalBatchUpdateCount);
  const dispatch = useDispatch();

  if (!count) {
    return null;
  }

  let status: LoadingStatus;
  let description: string;

  // 일괄 업데이트를 시작할 때 각 계약 업데이트 task를 비동기적으로 생성해 업데이트 시작 초기에 pending, failure, success가 모두 0이 올 수 있습니다.
  if (count.pending + count.success + count.failure === 0) {
    status = LoadingStatus.PENDING;
    description = '계약 정보 업데이트 준비 중';
  } else if (count.pending !== 0) {
    status = LoadingStatus.PENDING;
    description = `${count.pending}개 계약 정보 업데이트 중`;
    if (count.failure) {
      description += ` (${count.failure}개 실패)`;
    }
  } else if (count.failure) {
    status = LoadingStatus.FAILED;
    description = `계약 정보 업데이트 완료 (${count.failure}개 실패)`;
  } else {
    status = LoadingStatus.SUCCEEDED;
    description = `${count.success}개 계약 정보 업데이트 완료`;
  }

  return (
    <Box maxWidth="xs" p={4} width="min(450px, 100vw)">
      <Flex
        alignItems="center"
        bg="notificationBackgroundSecondaryUltraDark"
        borderRadius="4px"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.25)"
        justifyContent="space-between"
        pl={5}
        pr={4}
        py={5}
      >
        <Flex alignItems="center" flex="auto">
          <StatusIcon status={status} />
          <Text color="textNeutralLightest" fontWeight="medium" ml={4}>
            {description}
          </Text>
        </Flex>
        <TextLink
          sx={{
            color: 'textNeutralUltraLight',
            '&:hover': {
              color: 'textNeutralLightest',
            },
          }}
          to="/external-documents/batch-update"
          onClick={() => dispatch(initializeExternalDocumentBatchJob())}
        >
          내역 보기
          <Box as={FontAwesomeIcon} fontSize="12px" icon={faArrowUpRightFromSquare} ml="2px" />
        </TextLink>
        {(status === LoadingStatus.SUCCEEDED || status === LoadingStatus.FAILED) && (
          <TextButton
            ml={6}
            sx={{
              color: 'textNeutralUltraLight',
              '&:hover': {
                color: 'textNeutralLightest',
              },
            }}
            onClick={() => dispatch(initializeExternalDocumentBatchJob())}
          >
            닫기
          </TextButton>
        )}
      </Flex>
    </Box>
  );
}

function StatusIcon({ status }: { status: LoadingStatus }) {
  switch (status) {
    case LoadingStatus.PENDING:
      return (
        <Box color="iconNeutralLightest" fontSize="18px">
          <FontAwesomeIcon icon={faArrowsRotate} />
        </Box>
      );
    case LoadingStatus.SUCCEEDED:
      return (
        <Box color="iconPositiveBase" fontSize="18px">
          <FontAwesomeIcon icon={faCheckCircle} />
        </Box>
      );
    case LoadingStatus.FAILED:
      return (
        <Box color="iconNegativeBase" fontSize="18px">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </Box>
      );
    default:
      return null;
  }
}
